import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Row, Col, Input, Select } from 'antd';

import { BlueButton, OrangeOutlineButton } from '@components';
import { Credit, CreditCrop, CxCredit, CxFarm } from '@types';
import { CxOptions } from '@network';

interface Props {
  insurance: CreditCrop;
  credit: Credit;
  updateInsurance: (insurance: CreditCrop) => void;
  cxOptions: CxOptions;
  creditFarms: CxFarm[];
  setCreditFarms: (farms: CxFarm[]) => void;
  usedFarmIds: number[];
  idx: number;
  isDownloading?: boolean;
  shortEdit?: boolean;
  editable?: boolean;
}

export const PolicyFarms = (props: Props) => {
  const {
    insurance, updateInsurance, creditFarms, usedFarmIds, isDownloading, shortEdit, editable,
  } = props;
  const [farms, setFarms] = useState<CxFarm[]>(insurance.farms || []);
  const cls = useStyles();

  useEffect(() => {
    if (farms.length !== 0) return;

    const newFarm: CxFarm = {
      id: null,
      key: new Date().getTime().toString(),
      latitude: '',
      longitude: '',
    };
    const vacantFarm = creditFarms.find(farm => !farm.insurancePolicyId);
    const nextFarms = vacantFarm ? [vacantFarm] : [newFarm];

    updateInsurance({ ...insurance, farms: nextFarms });
    setFarms(nextFarms);
  }, [farms]);

  useEffect(() => void setFarms(insurance.farms ?? []), [insurance.farms]);

  const addFarm = () => {
    const newFarm: CxFarm = {
      id: null,
      key: new Date().getTime().toString(),
      latitude: '',
      longitude: '',
    };
    updateInsurance({
      ...insurance,
      farms: (insurance.farms || []).concat([newFarm]),
    });
  };

  const removeFarm = (key: string) => {
    updateInsurance({
      ...insurance,
      farms: (insurance.farms || []).filter(farm => farm.key !== key),
    });
  };

  return (
    <div className={cls.farms}>
      {farms.map((farm, farmIndex) => (
        <Row gutter={[20, 20]} key={farm.key}>
          <Col sm={6}>
            <Input
              placeholder="Latitude"
              disabled={!editable || shortEdit}
              className={cls.formInput}
              value={farm.latitude}
              onChange={e => updateInsurance({
                ...insurance,
                farms: (insurance.farms || []).map(insuranceFarm => {
                  if (insuranceFarm.key !== farm.key) return insuranceFarm;
                  insuranceFarm.latitude = e.target.value;
                  return insuranceFarm;
                }),
              })}
              status={farm.latitude ? undefined : 'error'}
            />
          </Col>
          <Col sm={6}>
            <Input
              placeholder="Longitude"
              disabled={!editable || shortEdit}
              className={cls.formInput}
              value={farm.longitude}
              onChange={e => updateInsurance({
                ...insurance,
                farms: (insurance.farms || []).map(insuranceFarm => {
                  if (insuranceFarm.key !== farm.key) return insuranceFarm;
                  insuranceFarm.longitude = e.target.value;
                  return insuranceFarm;
                }),
              })}
              status={farm.longitude ? undefined : 'error'}
            />
          </Col>
          <Col sm={12}>
            <div className={cls.farmActions}>
              <Select
                value={farm.id}
                disabled={!editable || shortEdit}
                onChange={farmId => updateInsurance({
                  ...insurance,
                  farms: (insurance.farms || []).map(insuranceFarm => {
                    if (insuranceFarm.key !== farm.key) return insuranceFarm;
                    insuranceFarm.id = farmId;
                    const existingFarm = creditFarms.find(farm => farm.id === farmId);
                    if (existingFarm) {
                      insuranceFarm.latitude = existingFarm.latitude || '';
                    }
                    if (existingFarm) {
                      insuranceFarm.longitude = existingFarm.longitude || '';
                    }
                    return insuranceFarm;
                  }),
                })}
                className={cls.farmSelect}
                options={[{
                  value: 0,
                  label: 'New Farm',
                }, ...creditFarms.map((creditFarm, i) => ({
                  value: creditFarm.id,
                  label: `Farm #${i + 1}`,
                  disabled: usedFarmIds.includes(creditFarm.id || 0) && creditFarm.id !== farm.id,
                })),
                ]}
              />
              {editable && !isDownloading && shortEdit === false && (
                <>
                  <OrangeOutlineButton
                    onClick={() => removeFarm(`${farm.key}`)}
                    className={cls.farmBtnRemove}
                  >x</OrangeOutlineButton>
                  {farmIndex === 0 && (
                    <BlueButton
                      onClick={addFarm}
                      className={cls.farmBtnAdd}
                    >+ Add Farm</BlueButton>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
      ))}
    </div>
  );
};

const useStyles = makeStyles({
  farms: {
    minHeight: 37,
  },
  farmActions: {
    display: 'flex',
    alignItems: 'center',
  },
  farmSelect: {
    width: 105,
    fontSize: 13,
    marginTop: -5,
    '& .ant-select-selector': {
      border: 'none !important',
      borderBottom: '1px solid rgba(0, 42, 119, 0.10) !important',
    },
    '&.ant-select-disabled .ant-select-selector': {
      backgroundColor: 'transparent!important',
    },
  },
  farmBtnRemove: {
    marginLeft: 8,
    minWidth: 28,
    width: 28,
    height: 28,
    padding: 0,
  },
  farmBtnAdd: {
    marginLeft: 8,
    minWidth: 90,
    width: 90,
    height: 28,
    padding: 0,
    fontSize: 14,
    borderRadius: 4,
  },
  formInput: {
    border: 'none',
    borderRadius: 0,
    borderBottom: '1px solid rgba(0, 42, 119, 0.10)',
    width: '100%',
    fontSize: '12px !important',
    '&:focus, &:hover, &[class~="ant-input-number-focused"], & [class~="ant-input-number-focused"], &.ant-input-number-affix-wrapper-focused': {
      boxShadow: 'none',
      borderBottom: '1px solid #002A77 !important',
      '& .ant-input-number-input-wrap': {
        border: 'none !important',
      },
    },
    '& .ant-picker-input': {
      border: 'none !important',
    },
    '&:disabled': {
      backgroundColor: 'transparent!important',
    },
  },
});
